import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy | VietClinics</title>
        <meta name="description" content="How VietClinics collects, uses and safeguards your personal information — patient health data, clinic data, third-party integrations and your rights." />
        <link rel="canonical" href="https://vietclinics.com/privacy" />
      </Helmet>
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] py-12">
        <div className="container mx-auto px-4 text-center">
          <Shield className="h-12 w-12 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Privacy Policy
          </h1>
          <p className="text-white/80">Last updated: March 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm p-6 md:p-10">
          <div className="prose prose-gray max-w-none">

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-600 leading-relaxed">
                VietClinics ("we," "our," or "us") is committed to protecting your privacy. This Privacy 
                Policy explains how we collect, use, disclose, and safeguard your information when you 
                use our website and services. Please read this policy carefully.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Personal Information</h3>
              <p className="text-gray-600 leading-relaxed mb-2">We may collect the following personal information:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li><strong>Identity Data:</strong> Name, date of birth, gender</li>
                <li><strong>Contact Data:</strong> Email address, phone number, WhatsApp number</li>
                <li><strong>Profile Data:</strong> Username, preferences, booking history</li>
                <li><strong>Health Information:</strong> Treatment interests, medical notes shared during booking</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">For Clinics</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>Business name and registration details</li>
                <li>Clinic location and contact information</li>
                <li>Doctor credentials and certifications</li>
                <li>Service offerings and pricing</li>
                <li>Photos of facilities</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-600 space-y-1">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent</li>
                <li>Referring website</li>
                <li>Location data (with your consent)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-600 leading-relaxed mb-2">We use collected information to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Provide Services:</strong> Process bookings, connect patients with clinics, send confirmations</li>
                <li><strong>Communication:</strong> Send booking updates, respond to inquiries, provide customer support</li>
                <li><strong>Improvement:</strong> Analyze usage patterns to improve our platform and services</li>
                <li><strong>Marketing:</strong> Send promotional offers and newsletters (with your consent)</li>
                <li><strong>Security:</strong> Detect and prevent fraud, protect user accounts</li>
                <li><strong>Legal Compliance:</strong> Comply with applicable laws and regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Data Sharing</h2>
              <p className="text-gray-600 leading-relaxed mb-2">We may share your information with:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Clinics:</strong> To process your booking requests (name, contact, treatment interest)</li>
                <li><strong>Service Providers:</strong> Payment processors, hosting providers, analytics services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>We do not sell your personal information to third parties.</strong>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Third-Party Integrations</h2>
              <p className="text-gray-600 leading-relaxed mb-2">Our platform may integrate with:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Payment Gateways:</strong> Stripe, VNPay for processing payments</li>
                <li><strong>Analytics:</strong> Google Analytics for usage analysis</li>
                <li><strong>Maps:</strong> Google Maps for location services</li>
                <li><strong>Communication:</strong> WhatsApp, email service providers</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-2">
                These third parties have their own privacy policies governing their use of your data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data Protection Measures</h2>
              <p className="text-gray-600 leading-relaxed mb-2">We implement security measures including:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>SSL/TLS encryption for data in transit</li>
                <li>Encrypted storage for sensitive data</li>
                <li>Access controls and authentication</li>
                <li>Regular security audits</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                While we strive to protect your information, no method of transmission over the internet 
                is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-600 leading-relaxed">
                We retain your personal information for as long as your account is active or as needed 
                to provide services. We may retain certain information for legal compliance, dispute 
                resolution, or legitimate business purposes. Booking records are retained for 5 years 
                for medical record-keeping purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
              <p className="text-gray-600 leading-relaxed mb-2">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Withdraw Consent:</strong> Withdraw previously given consent</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                To exercise these rights, contact us at privacy@vietclinics.com
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Cookies</h2>
              <p className="text-gray-600 leading-relaxed">
                We use cookies and similar technologies to enhance your experience, analyze usage, and 
                remember your preferences. You can manage cookie preferences through your browser settings. 
                Disabling cookies may affect some features of our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-600 leading-relaxed">
                Our services are not directed to children under 18. We do not knowingly collect information 
                from children. If you believe we have collected data from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy periodically. We will notify you of significant changes 
                via email or platform notification. Your continued use after changes constitutes acceptance 
                of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed">
                For privacy-related questions or to exercise your rights, contact our Data Protection Officer:
              </p>
              <ul className="list-none mt-2 text-gray-600">
                <li>Email: privacy@vietclinics.com</li>
                <li>Address: District 1, Ho Chi Minh City, Vietnam</li>
              </ul>
            </section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
